import { useState, useEffect } from "react";
import api from "../../utils/api";

import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";

const useDashboardData = () => {
  const [userFullName, setUserFullName] = useState("User");
  const [error, setError] = useState("");
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  );

  const [stats, setStats] = useState([]);
  const [maintenanceByMonth, setMaintenanceByMonth] = useState(null);
  const [workOrderChartData, setWorkOrderChartData] = useState(null);
  const [violationsByYearChart, setViolationsByYearChart] = useState(null);
  const [architecturalRequestsByYearChart, setArchitecturalRequestsByYearChart] =
    useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const date = new Date();
        const currentYear = date.getFullYear();
        const currentMonth = date.getMonth();

        const [maintenanceRes, violationsRes, archRes] = await Promise.all([
          api.get("/getWorkOrders"),
          api.get("/getViolations"),
          api.get("/architectural-requests"),
        ]);

        const maintenance = maintenanceRes.data || [];
        const violations = violationsRes.data || [];
        const archRequests = archRes.data || [];

        const totalWorkOrders = maintenance.length;
        const currentMonthWorkOrders = maintenance.filter((m) => {
          const created = new Date(m.created_at);
          return (
            created.getMonth() === currentMonth &&
            created.getFullYear() === currentYear
          );
        }).length;

        const resolvedThisMonth = maintenance.filter((m) => {
          const resolved = new Date(m.resolved_at || m.updated_at);
          return (
            m.status === "Resolved" &&
            resolved.getMonth() === currentMonth &&
            resolved.getFullYear() === currentYear
          );
        }).length;

        const resolutionTimes = maintenance
          .filter((m) => m.status === "Resolved" && m.created_at && m.resolved_at)
          .map(
            (m) =>
              (new Date(m.resolved_at) - new Date(m.created_at)) /
              (1000 * 60 * 60 * 24)
          );

        const avgResolutionTime =
          resolutionTimes.length > 0
            ? (
                resolutionTimes.reduce((a, b) => a + b, 0) / resolutionTimes.length
              ).toFixed(1)
            : "N/A";

        const totalViolations = violations.length;
        const currentMonthViolations = violations.filter((v) => {
          const created = new Date(v.created_at);
          return (
            created.getMonth() === currentMonth &&
            created.getFullYear() === currentYear
          );
        }).length;

        const resolvedViolationsThisMonth = violations.filter((v) => {
          const resolved = new Date(v.resolved_at || v.updated_at);
          return (
            v.status === "Violation Cured" &&
            resolved.getMonth() === currentMonth &&
            resolved.getFullYear() === currentYear
          );
        }).length;

        const violationResolutionTimes = violations
          .filter((v) => v.status === "Violation Cured" && v.created_at && v.updated_at)
          .map(
            (v) =>
              (new Date(v.updated_at) - new Date(v.created_at)) /
              (1000 * 60 * 60 * 24)
          );

        const avgViolationResolutionTime =
          violationResolutionTimes.length > 0
            ? (
                violationResolutionTimes.reduce((a, b) => a + b, 0) /
                violationResolutionTimes.length
              ).toFixed(1)
            : "N/A";

        const totalARs = archRequests.length;
        const currentMonthARs = archRequests.filter((a) => {
          const created = new Date(a.requested_at);
          return (
            created.getMonth() === currentMonth &&
            created.getFullYear() === currentYear
          );
        }).length;

        const resolvedARsThisMonth = archRequests.filter((a) => {
          const resolved = new Date(a.resolved_at || a.updated_at);
          return (
            a.status === "Resolved" &&
            resolved.getMonth() === currentMonth &&
            resolved.getFullYear() === currentYear
          );
        }).length;

        const archResolutionTimes = archRequests
          .filter((a) => a.status === "Resolved" && a.requested_at && a.resolved_at)
          .map(
            (a) =>
              (new Date(a.resolved_at) - new Date(a.requested_at)) /
              (1000 * 60 * 60 * 24)
          );

        const avgARResolutionTime =
          archResolutionTimes.length > 0
            ? (
                archResolutionTimes.reduce((a, b) => a + b, 0) /
                archResolutionTimes.length
              ).toFixed(1)
            : "N/A";

        const statPalette = {
          primary: "#2f63ad",
          info: "#4f8df7",
          success: "#43a047",
          neutral: "#607d8b",
          danger: "#e53935",
          warning: "#fb8c00",
          olive: "#7cb342",
          brown: "#8d6e63",
          indigo: "#5c6bc0",
          teal: "#26a69a",
        };

        const stats = [
          {
            icon: <HomeRepairServiceOutlinedIcon fontSize="small" />,
            title: "Total Work Orders",
            count: totalWorkOrders,
            backgroundColor: statPalette.primary,
            moduleId: 1,
          },
          {
            icon: <CalendarMonthOutlinedIcon fontSize="small" />,
            title: "Work Orders This Month",
            count: currentMonthWorkOrders,
            backgroundColor: statPalette.info,
            moduleId: 1,
          },
          {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            title: "Resolved Work Orders",
            count: resolvedThisMonth,
            backgroundColor: statPalette.success,
            moduleId: 1,
          },
          {
            icon: <ScheduleOutlinedIcon fontSize="small" />,
            title: "Avg. WO Resolution",
            count: avgResolutionTime === "N/A" ? "N/A" : `${avgResolutionTime} days`,
            backgroundColor: statPalette.neutral,
            moduleId: 1,
          },

          {
            icon: <GavelOutlinedIcon fontSize="small" />,
            title: "Total Violations",
            count: totalViolations,
            backgroundColor: statPalette.danger,
            moduleId: 2,
          },
          {
            icon: <CalendarMonthOutlinedIcon fontSize="small" />,
            title: "Violations This Month",
            count: currentMonthViolations,
            backgroundColor: statPalette.warning,
            moduleId: 2,
          },
          {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            title: "Resolved Violations",
            count: resolvedViolationsThisMonth,
            backgroundColor: statPalette.olive,
            moduleId: 2,
          },
          {
            icon: <ScheduleOutlinedIcon fontSize="small" />,
            title: "Avg. Violation Resolution",
            count:
              avgViolationResolutionTime === "N/A"
                ? "N/A"
                : `${avgViolationResolutionTime} days`,
            backgroundColor: statPalette.brown,
            moduleId: 2,
          },

          {
            icon: <ArchitectureOutlinedIcon fontSize="small" />,
            title: "Total ARs",
            count: totalARs,
            backgroundColor: statPalette.indigo,
            moduleId: 3,
          },
          {
            icon: <CalendarMonthOutlinedIcon fontSize="small" />,
            title: "ARs This Month",
            count: currentMonthARs,
            backgroundColor: statPalette.info,
            moduleId: 3,
          },
          {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            title: "Resolved ARs",
            count: resolvedARsThisMonth,
            backgroundColor: statPalette.success,
            moduleId: 3,
          },
          {
            icon: <ScheduleOutlinedIcon fontSize="small" />,
            title: "Avg. AR Resolution",
            count: avgARResolutionTime === "N/A" ? "N/A" : `${avgARResolutionTime} days`,
            backgroundColor: statPalette.teal,
            moduleId: 3,
          },
        ];

        const monthLabels = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];

        const yearMap = {};
        maintenance.forEach((m) => {
          const d = new Date(m.created_at);
          const y = d.getFullYear();
          const mth = d.getMonth();
          if (!yearMap[y]) yearMap[y] = Array(12).fill(0);
          yearMap[y][mth]++;
        });

        const colorPalette = [
          "#2f63ad",
          "#4f8df7",
          "#43a047",
          "#fb8c00",
          "#e53935",
          "#7e57c2",
          "#26a69a",
          "#8d6e63",
          "#5c6bc0",
          "#ec407a",
        ];

        const maintenanceByMonth = {
          labels: monthLabels,
          datasets: Object.entries(yearMap).map(([year, yearData], idx) => ({
            label: year,
            data: yearData,
            borderColor: colorPalette[idx % colorPalette.length],
            backgroundColor: `${colorPalette[idx % colorPalette.length]}22`,
            pointBackgroundColor: colorPalette[idx % colorPalette.length],
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            tension: 0.35,
            fill: false,
          })),
        };

        const statusMap = {};
        maintenance.forEach((order) => {
          const status = order.status || "Unknown";
          statusMap[status] = (statusMap[status] || 0) + 1;
        });

        const statusColors = {
          Open: "#4f8df7",
          "In Progress": "#fb8c00",
          Hold: "#7e57c2",
          Resolved: "#43a047",
          Closed: "#607d8b",
          Unknown: "#b0bec5",
        };

        const workOrderChartData = {
          labels: Object.keys(statusMap),
          datasets: [
            {
              label: "Work Orders by Status",
              data: Object.values(statusMap),
              backgroundColor: Object.keys(statusMap).map(
                (status) => statusColors[status] || "#d0d7de"
              ),
              borderColor: Object.keys(statusMap).map(
                (status) => statusColors[status] || "#d0d7de"
              ),
              borderWidth: 1,
            },
          ],
        };

        const buildYearChart = (items, dateKey) => {
          const map = {};
          items.forEach((item) => {
            const date = new Date(item[dateKey]);
            if (isNaN(date)) return;
            const year = date.getFullYear();
            const month = date.getMonth();
            if (!map[year]) map[year] = Array(12).fill(0);
            map[year][month]++;
          });

          return {
            labels: monthLabels,
            datasets: Object.entries(map).map(([year, yearData], idx) => ({
              label: year,
              data: yearData,
              borderColor: colorPalette[idx % colorPalette.length],
              backgroundColor: `${colorPalette[idx % colorPalette.length]}22`,
              pointBackgroundColor: colorPalette[idx % colorPalette.length],
              pointBorderColor: "#ffffff",
              pointBorderWidth: 2,
              tension: 0.35,
              fill: false,
            })),
          };
        };

        const violationsByYearChart = buildYearChart(violations, "created_at");
        const architecturalRequestsByYearChart = buildYearChart(
          archRequests,
          "requested_at"
        );

        setStats(stats);
        setMaintenanceByMonth(maintenanceByMonth);
        setWorkOrderChartData(workOrderChartData);
        setViolationsByYearChart(violationsByYearChart);
        setArchitecturalRequestsByYearChart(architecturalRequestsByYearChart);

        const firstName = localStorage.getItem("firstName") || "Admin";
        const lastName = localStorage.getItem("lastName") || "User";
        setUserFullName(`${firstName} ${lastName}`);
      } catch (err) {
        console.error("❌ Failed to fetch dashboard data:", err);
        setError("Failed to load dashboard data. Please refresh the page.");
      }
    };

    fetchData();

    const interval = setInterval(() => {
      setCurrentTime(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return {
    userFullName,
    currentTime,
    stats,
    maintenanceByMonth,
    workOrderChartData,
    violationsByYearChart,
    architecturalRequestsByYearChart,
    error,
  };
};

export default useDashboardData;
